// Не audio.volume напрямую: на iOS присваивание игнорируется, и слайдер
// громкости музыки там ни на что не влиял
import {getAudioElementVolume, releaseAudioElementVolume, setAudioElementVolume} from './audioElementVolume';

// Плавное появление и затухание музыки: старт, остановка, смена трека и
// последние секунды трека перед следующим
export const MUSIC_FADE_DURATION_MS = 2000;
// Подстройка под ползунок громкости — быстрее: игрок слушает результат
export const MUSIC_VOLUME_CHANGE_FADE_MS = 500;
// Шаг плавного изменения громкости и проверки конца трека
export const MUSIC_UPDATE_INTERVAL_MS = 50;

// Программы из одного трека: на трек одна, чтобы ее можно было сравнивать
const loopPrograms = new WeakMap();

/**
 * Программа из одного трека по кругу, без паузы на стыке (музыка меню)
 * @param {{src: string, volume: number}} track
 */
export const createLoopMusicProgram = (track) => {
  if (!loopPrograms.has(track)) {
    loopPrograms.set(track, Object.freeze({
      key: track,
      getGapMs: () => 0,
      getNextItem: () => ({track}),
    }));
  }
  return loopPrograms.get(track);
};

/**
 * Проигрыватель фоновой музыки: звучит одна программа за раз — очередь треков
 * с плавными переходами. Ему говорят, ЧТО должно звучать (setDesired:
 * программа и играет ли она), а как туда прийти из текущего состояния, он
 * решает сам:
 *
 *  - та же программа, пора замолчать — плавно гасит и ставит на паузу;
 *  - та же программа, пора играть — продолжает трек с того же места и плавно
 *    проявляет (в партии музыка может молчать на своем ходу, см.
 *    MATCH_MUSIC_SILENT_ON_OWN_TURN: без этого длинный трек каждый чужой ход
 *    начинался бы заново и до следующего дело бы не дошло);
 *  - другая программа — сначала гасит звучащий трек, затем загружает первый
 *    трек новой программы и запускает его, если ему пора играть.
 *
 * Программа — {key, getGapMs(finished), getNextItem(previous)}:
 *  - key — по нему (===) проигрыватель узнает ту же программу. Объект
 *    программы с тем же ключом может смениться (в партии начался новый блок
 *    раундов) — звучащий трек не обрывается, следующий выберет новый объект;
 *  - getNextItem(previous) — что звучит следующим: {track, ...}; previous —
 *    то, что отдал прошлый вызов (null — начало программы). Зовется, когда
 *    программа начинается и когда пора запускать следующий трек;
 *  - getGapMs(finished) — тишина после доигравшего трека finished (то, что
 *    отдал для него getNextItem). Последние секунды трек гаснет, затем пауза,
 *    затем следующий трек проявляется с начала; 0 — без паузы, как трек по
 *    кругу.
 *
 * Решение можно менять в любой момент, в том числе посреди затухания: вошли в
 * партию и тут же вышли — музыка меню не останавливается и не начинается
 * заново, а возвращает громкость.
 *
 * Элемент Audio ОДИН на все треки, меняется только его источник. Так нужно
 * для iOS: запускать звук без жеста пользователя там можно только у элемента,
 * который уже был запущен жестом. Музыка партии включается сама, когда ход
 * переходит сопернику, — у отдельного, ни разу не запущенного жестом элемента
 * она на айфоне не заиграла бы никогда.
 *
 * @param {Object} options
 * @param {Function} options.getVolume - громкость музыки из настроек, 0..1
 * @param {Function} [options.createAudio] - фабрика элемента (для тестов)
 */
export const createBackgroundMusicPlayer = ({getVolume, createAudio = () => new Audio()}) => {
  const audio = createAudio();
  setAudioElementVolume(audio, 0);

  // Что должно звучать. До первого setDesired программы нет
  let desiredProgram = null;
  let isDesiredPlaying = false;
  // Программа, трек которой загружен в элемент, и что она отдала для него
  // ({track, ...})
  let loadedProgram = null;
  let currentItem = null;
  // Трек в элементе. Тот же трек следующим (трек меню по кругу) источник не
  // перезагружает — только перематывает в начало
  let loadedTrack = null;
  // Трек доигран: ждем паузы между треками либо разрешения играть
  let isTrackFinished = false;

  let fadeTimer = null;
  let endCheckTimer = null;
  let gapTimer = null;
  // Идет затухание перед паузой
  let isStopping = false;
  // Идет затухание к концу трека
  let isTrackEnding = false;
  // Запуск начат, но браузер еще не ответил (файл грузится). Трек при этом
  // числится играющим, хотя его еще не слышно
  let isStarting = false;
  // Отличает актуальный запуск от запуска, который успели отменить, пока
  // браузер начинал воспроизведение
  let playToken = 0;
  // Элемент хоть раз запущен успешно. До этого его запуск без жеста браузер
  // может блокировать (см. handleUserGesture)
  let isUnlocked = false;
  let isDestroyed = false;

  const isSameProgram = (left, right) => (left && right ? left.key === right.key : left === right);

  const getTargetVolume = () => (currentItem ? currentItem.track.volume * getVolume() : 0);

  // Запуск воспроизведения. play() зовется синхронно: внутри обработчика клика
  // это и есть «запуск жестом», который разблокирует звук. Старые встроенные
  // браузеры отдают из play() не обещание, а undefined, или бросают исключение
  const play = () => {
    try {
      return Promise.resolve(audio.play());
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const clearFade = () => {
    if (fadeTimer) {
      clearInterval(fadeTimer);
      fadeTimer = null;
    }
  };

  const clearGap = () => {
    if (gapTimer) {
      clearTimeout(gapTimer);
      gapTimer = null;
    }
  };

  const fadeTo = (targetVolume, durationMs, onComplete) => {
    clearFade();
    const startVolume = getAudioElementVolume(audio);
    const steps = Math.max(1, Math.round(durationMs / MUSIC_UPDATE_INTERVAL_MS));
    let currentStep = 0;

    fadeTimer = setInterval(() => {
      currentStep += 1;
      setAudioElementVolume(audio, startVolume + (targetVolume - startVolume) * (currentStep / steps));

      if (currentStep >= steps) {
        clearFade();
        setAudioElementVolume(audio, targetVolume);
        if (onComplete) onComplete();
      }
    }, MUSIC_UPDATE_INTERVAL_MS);
  };

  const stopEndCheck = () => {
    if (endCheckTimer) {
      clearInterval(endCheckTimer);
      endCheckTimer = null;
    }
    isTrackEnding = false;
  };

  // Снимает все начатое: затухание, проверку конца трека, ждущий ответа
  // запуск. Элемент после этого молчит, и следующий запуск начнется с чистого
  // листа
  const resetTransitions = () => {
    clearFade();
    stopEndCheck();
    isStopping = false;
    isStarting = false;
    playToken += 1;
    setAudioElementVolume(audio, 0);
  };

  // Скорость трека (track.playbackRate, по умолчанию 1) и сохранение высоты
  // тона при ней (track.preservesPitch, по умолчанию да). Ставится на каждый
  // загруженный трек: загрузка нового источника сбрасывает playbackRate к
  // defaultPlaybackRate, а следующий трек может быть без замедления.
  // preservesPitch у старых WebKit и Firefox — с префиксом
  const applyTrackPlayback = (track) => {
    const rate = track.playbackRate > 0 ? track.playbackRate : 1;
    const preservesPitch = track.preservesPitch !== false;
    try {
      audio.defaultPlaybackRate = rate;
      audio.playbackRate = rate;
      audio.preservesPitch = preservesPitch;
      audio.webkitPreservesPitch = preservesPitch;
      audio.mozPreservesPitch = preservesPitch;
    } catch (error) {
      // Браузер не принял скорость — трек играет как записан
    }
  };

  // Загружает в элемент то, что отдала программа, — с начала трека
  const loadItem = (item) => {
    currentItem = item;
    isTrackFinished = false;
    if (item.track !== loadedTrack) {
      loadedTrack = item.track;
      audio.src = loadedTrack.src;
    }
    applyTrackPlayback(item.track);
    audio.currentTime = 0;
  };

  // Трек доигран: гаснет к концу сам (finishTrack по проверке конца) либо
  // браузер прислал ended. Следующий трек — после паузы программы, если
  // музыке все еще пора играть
  const finishTrack = () => {
    resetTransitions();
    if (!audio.paused) {
      audio.pause();
    }
    isTrackFinished = true;
    clearGap();
    if (isDesiredPlaying && loadedProgram && isSameProgram(loadedProgram, desiredProgram) && currentItem) {
      // Паузу решает свежий объект программы: в партии он знает текущий блок
      const gapMs = desiredProgram.getGapMs(currentItem);
      if (gapMs > 0) {
        gapTimer = setTimeout(() => {
          gapTimer = null;
          sync();
        }, gapMs);
      }
    }
    sync();
  };

  // У конца трека: за время затухания до конца он гаснет, и программа
  // переходит к следующему треку (трек меню — к самому себе, без щелчка на
  // стыке)
  const startEndCheck = () => {
    stopEndCheck();

    endCheckTimer = setInterval(() => {
      if (!audio.duration || audio.paused || isTrackEnding) return;

      // Сколько осталось играть по часам, а не по времени трека: замедленный
      // трек (playbackRate < 1) доигрывает свой остаток дольше, и без поправки
      // затухание кончалось бы раньше трека — хвост обрывался бы
      const playbackRate = audio.playbackRate > 0 ? audio.playbackRate : 1;
      const timeRemainingMs = ((audio.duration - audio.currentTime) * 1000) / playbackRate;
      if (timeRemainingMs > MUSIC_FADE_DURATION_MS) return;

      isTrackEnding = true;
      fadeTo(0, Math.max(0, timeRemainingMs), finishTrack);
    }, MUSIC_UPDATE_INTERVAL_MS);
  };

  const startPlayback = () => {
    resetTransitions();
    const token = playToken;
    isStarting = true;

    play().then(() => {
      isUnlocked = true;
      if (token !== playToken || isDestroyed) return;
      isStarting = false;
      if (!isDesiredPlaying || !isSameProgram(loadedProgram, desiredProgram)) {
        // Запуск был нужен только чтобы разблокировать элемент жестом, либо
        // решение успело смениться: приводим к нужному (трек еще не слышен)
        sync();
        return;
      }
      fadeTo(getTargetVolume(), MUSIC_FADE_DURATION_MS);
      startEndCheck();
    }).catch(() => {
      if (token !== playToken || isDestroyed) return;
      isStarting = false;
      // Автовоспроизведение заблокировано либо файл не загрузился. Элемент мог
      // остаться «играющим» без звука — возвращаем его на паузу, чтобы
      // следующий жест повторил запуск (handleUserGesture)
      if (!audio.paused) {
        audio.pause();
      }
    });
  };

  // Ставит звучащий трек на паузу. Если его слышно — сначала плавно гасит
  const stopPlayback = () => {
    if (isStopping) return;

    stopEndCheck();
    // Ждущий ответа запуск, если он был, отменен
    isStarting = false;
    playToken += 1;

    const pause = () => {
      isStopping = false;
      audio.pause();
      sync();
    };

    if (getAudioElementVolume(audio) <= 0) {
      // Гасить нечего: трек еще не успел проявиться либо музыка выключена
      // ползунком. Ждать незачем
      clearFade();
      pause();
      return;
    }

    isStopping = true;
    fadeTo(0, MUSIC_FADE_DURATION_MS, pause);
  };

  // Приводит проигрыватель к тому, что должно звучать
  const sync = () => {
    if (isDestroyed) return;

    if (audio.paused && (isStopping || isTrackEnding || fadeTimer || endCheckTimer)) {
      // Элемент встал на паузу не по воле проигрывателя: трек доиграл до
      // конца либо звук прервала система (звонок). Начатые затухание и
      // проверка конца потеряли смысл — без сброса их флаги запретили бы
      // следующую остановку
      resetTransitions();
    }

    if (!isSameProgram(loadedProgram, desiredProgram)) {
      if (!audio.paused) {
        // В элементе звучит трек другой программы: менять источник можно
        // только после паузы. По ее наступлении sync вызовется снова
        stopPlayback();
        return;
      }
      clearGap();
      loadedProgram = desiredProgram;
      currentItem = null;
      isTrackFinished = false;
      if (loadedProgram) {
        loadItem(loadedProgram.getNextItem(null));
      }
    } else {
      // Та же программа, но объект ее мог смениться (новый блок раундов):
      // следующий трек выберет уже он
      loadedProgram = desiredProgram;
    }

    if (!loadedProgram) return;

    if (!isTrackFinished && audio.paused && audio.ended) {
      // Трек доиграл, а ended еще не обработан: запуск доигравшего элемента
      // начал бы тот же трек заново вместо следующего
      isTrackFinished = true;
    }

    if (isTrackFinished) {
      if (!isDesiredPlaying) {
        // Пауза между треками пришлась на тишину (свой ход): разрешат играть —
        // следующий трек начнется сразу, тишина уже была
        clearGap();
        return;
      }
      if (gapTimer) return;
      loadItem(loadedProgram.getNextItem(currentItem));
    }

    if (isDesiredPlaying) {
      if (audio.paused) {
        startPlayback();
      } else if (isStopping) {
        // Передумали останавливать: возвращаем громкость, трек идет дальше
        isStopping = false;
        fadeTo(getTargetVolume(), MUSIC_FADE_DURATION_MS);
        startEndCheck();
      }
    } else if (!audio.paused) {
      stopPlayback();
    }
  };

  // Трек доиграл до конца раньше, чем сработала проверка конца (таймеры
  // фоновой вкладки браузер придерживает): переходим к следующему сразу, не
  // дожидаясь жеста
  const handleEnded = () => {
    if (isDestroyed) return;
    if (isTrackFinished) {
      sync();
      return;
    }
    finishTrack();
  };
  if (audio.addEventListener) {
    audio.addEventListener('ended', handleEnded);
  }

  return {
    /**
     * Что должно звучать
     * @param {Object} desired
     * @param {{key: *, getGapMs: Function, getNextItem: Function}|null} desired.program -
     *   программа (createLoopMusicProgram, getMatchMusicProgram). Программы
     *   сравниваются по key. null — тишина: звучащий трек гаснет, а новый не
     *   загружается (в партии — пока не загрузилось поле)
     * @param {boolean} desired.isPlaying - играет ли она сейчас
     */
    setDesired({program, isPlaying}) {
      desiredProgram = program;
      isDesiredPlaying = Boolean(isPlaying);
      sync();
    },

    /**
     * Жест пользователя (клик, касание, клавиша). Повторяет запуск, который
     * браузер заблокировал до первого взаимодействия со страницей, и
     * возвращает музыку, которую прервала система.
     *
     * Если музыке сейчас положено молчать, а элемент еще ни разу не был
     * запущен, жест все равно запускает его — беззвучно, и тут же ставит на
     * паузу. Иначе на телефоне после перезагрузки страницы посреди партии
     * музыка не включилась бы, когда ход перейдет сопернику: запуск без жеста
     * разрешен только элементу, который уже запускали жестом
     */
    handleUserGesture() {
      // Запуск уже ждет ответа браузера. На одно касание приходит несколько
      // событий жеста подряд, и второе не должно обрывать начатое первым
      if (isDestroyed || !currentItem || isStarting) return;

      if (!isUnlocked && audio.paused && !isDesiredPlaying && !isTrackFinished
        && isSameProgram(loadedProgram, desiredProgram)) {
        startPlayback();
        return;
      }
      sync();
    },

    /**
     * Ползунок громкости музыки сдвинули — подстраиваем звучащий трек.
     * Гаснущий трек и еще не начавшийся запуск не трогаем: они сами проявят
     * трек уже с новой громкостью
     */
    refreshVolume() {
      if (isDestroyed || audio.paused || isStopping || isTrackEnding || isStarting) return;
      fadeTo(getTargetVolume(), MUSIC_VOLUME_CHANGE_FADE_MS);
    },

    /**
     * Останавливает музыку и освобождает элемент. После этого проигрыватель
     * ни на что не реагирует
     */
    destroy() {
      isDestroyed = true;
      playToken += 1;
      clearFade();
      clearGap();
      stopEndCheck();
      isStopping = false;
      isStarting = false;
      if (audio.removeEventListener) {
        audio.removeEventListener('ended', handleEnded);
      }
      audio.pause();
      audio.src = '';
      releaseAudioElementVolume(audio);
    },
  };
};
